import { create } from 'zustand';
import { adminEsQueryLogApi } from '@api/api-config';
import {
  EsQueryLogListResponse,
  EsQueryLogDetailResponse,
  EsQueryLogDashboardResponse,
} from '@api/generated/portal-be-admin';

interface PaginationInfo {
  page: number;
  size: number;
  total: number;
  totalPages: number;
}

interface Filters {
  page: number;
  size: number;
  queryType?: string;
  requestId?: string;
  userId?: number;
  minDurationMs?: number;
  minResponseSize?: number;
  requestHash?: number;
  dateFrom?: string;
  dateTo?: string;
}

interface EsQueryLogStoreState {
  loading: boolean;
  error: string | null;
  data: EsQueryLogListResponse[];
  pagination: PaginationInfo | null;
  detail: EsQueryLogDetailResponse | null;
  detailLoading: boolean;
  dashboard: EsQueryLogDashboardResponse | null;
  dashboardLoading: boolean;

  loadLogs: (filters: Filters) => Promise<void>;
  loadDetail: (id: number) => Promise<void>;
  loadDashboard: (hours?: number) => Promise<void>;
  clearDetail: () => void;
}

export const useEsQueryLogStore = create<EsQueryLogStoreState>(set => ({
  loading: false,
  error: null,
  data: [],
  pagination: null,
  detail: null,
  detailLoading: false,
  dashboard: null,
  dashboardLoading: false,

  loadLogs: async (filters: Filters) => {
    set({ loading: true, error: null });
    try {
      const res = await adminEsQueryLogApi.listLogs({
        page: filters.page,
        size: filters.size,
        queryType: filters.queryType,
        requestId: filters.requestId,
        userId: filters.userId,
        minDurationMs: filters.minDurationMs,
        minResponseSize: filters.minResponseSize,
        requestHash: filters.requestHash,
        dateFrom: filters.dateFrom ? new Date(filters.dateFrom).toISOString() : undefined,
        dateTo: filters.dateTo ? new Date(filters.dateTo).toISOString() : undefined,
      });
      set({
        data: res.data.items || [],
        pagination: { page: res.data.page, size: res.data.size, total: res.data.total, totalPages: res.data.totalPages },
        loading: false,
      });
    } catch (e: any) {
      set({ error: e.message, loading: false });
    }
  },

  loadDetail: async (id: number) => {
    set({ detailLoading: true, detail: null });
    try {
      const res = await adminEsQueryLogApi.getDetail({ id });
      set({ detail: res.data, detailLoading: false });
    } catch {
      set({ detailLoading: false });
    }
  },

  loadDashboard: async (hours = 24) => {
    set({ dashboardLoading: true });
    try {
      const res = await adminEsQueryLogApi.getDashboard({ hours });
      set({ dashboard: res.data, dashboardLoading: false });
    } catch {
      set({ dashboardLoading: false });
    }
  },

  clearDetail: () => set({ detail: null }),
}));
